/**
 * Ember's weekly letter as the page draws it (§4.20, 2026-09-23 二次拍板).
 *
 * Brain writes the letter as prose with three typesetting marks (ps
 * weekly_letter.txt OUTPUT): the person's own words in curly quotes followed
 * by the day in square brackets — “giving up” [Sept 9]; the question's
 * paragraph beginning with "? "; a movement as two lines of their own,
 * FROM [day] “…” / TO [day] “…”. This module reads those marks back into
 * blocks the page draws — a paragraph of runs (plain text and marked words
 * with their day), the movement's two ends, the question — and never shows
 * a mark. The conversation's turn carries brain's `plain` reading instead,
 * so the cover's first line is read from prose.
 */

export type LetterRun = { kind: 'text'; text: string } | { kind: 'words'; text: string; day: string | null };
export type LetterBlock =
  | { kind: 'para'; runs: LetterRun[] }
  | { kind: 'move'; from: { day: string | null; text: string }; to: { day: string | null; text: string } }
  | { kind: 'question'; text: string };

const QUOTE_RE = /[“"]([^“”"]+)[”"]\s*(?:\[([^\]]+)\])?/g;

/** A paragraph's text into runs: the person's own words (quoted, with their day) and the prose between. */
export function letterRuns(text: string): LetterRun[] {
  const runs: LetterRun[] = [];
  let last = 0;
  for (const m of text.matchAll(QUOTE_RE)) {
    const at = m.index ?? 0;
    if (at > last) runs.push({ kind: 'text', text: text.slice(last, at) });
    runs.push({ kind: 'words', text: m[1].trim(), day: m[2] ? m[2].trim() : null });
    last = at + m[0].length;
  }
  if (last < text.length) runs.push({ kind: 'text', text: text.slice(last) });
  return runs;
}

function movementEnd(line: string): { day: string | null; text: string } {
  const m = line.match(/^(?:FROM|TO)\s+(?:\[([^\]]+)\]\s*)?(.*)$/i);
  const raw = (m?.[2] || '').trim().replace(/^[“"]|[”"]$/g, '').trim();
  return { day: m?.[1]?.trim() || null, text: raw };
}

/** The letter's paragraphs into blocks. */
export function parseLetter(letter: string): LetterBlock[] {
  const paragraphs = String(letter || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const blocks: LetterBlock[] = [];
  for (const p of paragraphs) {
    const lines = p.split('\n').map((l) => l.trim()).filter(Boolean);
    const from = lines.find((l) => /^FROM\s/i.test(l));
    const to = lines.find((l) => /^TO\s/i.test(l));
    if (from && to && lines.length === 2) {
      blocks.push({ kind: 'move', from: movementEnd(from), to: movementEnd(to) });
      continue;
    }
    if (/^\?\s+/.test(p)) {
      blocks.push({ kind: 'question', text: p.replace(/^\?\s+/, '').replace(/\s*\[([^\]]+)\]/g, '') });
      continue;
    }
    blocks.push({ kind: 'para', runs: letterRuns(p) });
  }
  return blocks;
}

/** The letter as one line of prose — the cover's first sentence, clipped by the CSS. Marks read back: [day] dropped, "? " dropped, a movement as a sentence. */
export function letterFirstLine(letter: string): string {
  const first = parseLetter(letter)[0];
  if (!first) return '';
  const text = first.kind === 'para' ? first.runs.map((r) => (r.kind === 'words' ? `“${r.text}”` : r.text)).join('')
    : first.kind === 'move' ? `From “${first.from.text}” to “${first.to.text}”.`
      : first.text;
  const s = text.replace(/\s+/g, ' ').trim();
  const m = s.match(/^.+?[.!?。！？](\s|$)/);
  return (m ? m[0] : s).trim();
}

/** A conversation turn's prose (brain's plain reading) — its first sentence. */
export function firstSentence(text: string): string {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  const m = s.match(/^.+?[.!?。！？](\s|$)/);
  return (m ? m[0] : s).trim();
}

/** "Tuesday, September 23" — the day a letter is dated, no time. The year only when it is not this one. */
export function letterDateLine(iso: string | null | undefined, style: 'long' | 'short' = 'long'): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', {
    weekday: style === 'long' ? 'long' : 'short', month: style === 'long' ? 'long' : 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** "Sept 9 – 23" / "Aug 30 – Sept 9": the days a letter reads, from a YYYY-MM-DD PT day to the letter's day. */
export function readsLine(fromDay: string | null | undefined, writtenAt: string | null | undefined): string {
  if (!fromDay || !writtenAt) return '';
  const a = new Date(`${fromDay}T12:00:00Z`);
  const b = new Date(writtenAt);
  if (!Number.isFinite(a.getTime()) || !Number.isFinite(b.getTime())) return '';
  const monthDay = (d: Date, tz?: string): string => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(tz ? { timeZone: tz } : {}) });
  const left = monthDay(a, 'UTC');
  const right = monthDay(b);
  const sameMonth = left.split(' ')[0] === right.split(' ')[0];
  return `${left} – ${sameMonth ? right.split(' ')[1] : right}`;
}

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth'];
/** "Your third letter"; past twelve, "Your 13th letter". */
export function ordinalLine(n: number | null | undefined): string {
  if (!n || n < 1) return 'A letter';
  if (n <= ORDINALS.length) return `Your ${ORDINALS[n - 1]} letter`;
  const suffix = n % 10 === 1 && n % 100 !== 11 ? 'st' : n % 10 === 2 && n % 100 !== 12 ? 'nd' : n % 10 === 3 && n % 100 !== 13 ? 'rd' : 'th';
  return `Your ${n}${suffix} letter`;
}

/**
 * An hour on this browser's clock, said from now inside a sentence: "tonight at
 * 9:30 PM" / "today at 2:00 PM" / "tomorrow at 9:30 PM" / "on Thursday at 8:00 PM"
 * (2026-09-30 拍板: the hour a room opens at, set in UTC by the server and read
 * here where the person is). The words the Chats list says (timezone.js sayTime)
 * since 2026-10-22: "The room opens Tonight, 5:00 PM." read as a label dropped
 * into a sentence, beside the list's "opens tonight at 5:00 PM".
 */
export function hourLine(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const day = (x: Date): string => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  const tomorrow = new Date(now.getTime() + 86400e3);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (day(d) === day(now)) return `${d.getHours() >= 17 ? 'tonight' : 'today'} at ${time}`;
  if (day(d) === day(tomorrow)) return `tomorrow at ${time}`;
  return `on ${d.toLocaleDateString('en-US', { weekday: 'long' })} at ${time}`;
}
